import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import PageForm from '../../templates/PageForm';
import TextEdit from '../../molecules/TextEdit';
import SelectTextField, { OptionProps } from '../../molecules/SelectTextField';
import MultiEmailInput from '../communication/MultiEmailInput';
import Loading from '../../ions/Loading';
import { collateralApi, ApiError } from '../../../services/collateralApi';
import { COLLATERAL_BREADCRUMBS, formatCurrency } from './collateralUtils';

const toOptions = (items: string[]): OptionProps[] => items.map(i => ({ value: i, label: i }));

const GUARANTEE_STATUSES: OptionProps[] = [
    { value: 'NOT_STARTED', label: 'Não iniciado' },
    { value: 'IN_PROGRESS', label: 'Em andamento' },
    { value: 'CHECKED', label: 'Conferido' },
    { value: 'DONE', label: 'Concluído' },
    { value: 'SETTLED', label: 'Quitada' },
    { value: 'PAUSADO', label: 'Pausado' },
    { value: 'ACE', label: 'ACE' },
    { value: 'N/A', label: 'N/A' },
];
const TRADER_STATUSES: OptionProps[] = [
    { value: 'CONCLUIDO', label: 'Concluído' },
    { value: 'AWAITING_DOC', label: 'Aguardando doc' },
    { value: 'HAS_PENDENCY', label: 'Tem pendência' },
    { value: 'N/A', label: 'N/A' },
];
const DOC_STATUSES = toOptions(['OBRIGATORIO', 'SIM', 'SIM, ANTES', 'NÃO', 'CONTRATO', 'N/A']);
const SEFAZ_STATUSES = toOptions(['FAZER', 'FEITA', 'N/A']);
const CRITERIA_OPTIONS = toOptions(['Completa', 'Amostragem', 'N/A']);
const OPERATION_TYPES = toOptions(['CPRF', 'CPRF-ADIT', 'CESSAO', 'CESSAO-CD', 'CD', 'FLOW']);
const OBLIGATION_TYPES: OptionProps[] = [
    { value: 'WITH_RECOURSE', label: 'COM REGRESSO' },
    { value: 'WITHOUT_RECOURSE', label: 'SEM REGRESSO' },
    { value: 'NO_OBLIGATION', label: 'SEM GARANTIA' },
];
const COLLATERAL_SAVED_OPTIONS = toOptions(['Sim', 'NÃO TEM', 'Quitada', 'Promessa', 'Renegociado', 'N/A']);
const DEBTOR_NOTIFICATION_OPTIONS = toOptions(['Agrolend', 'n/a', 'confirmar', 'FEITA CEDENTE']);
const CONTRACT_REGISTER_OPTIONS = toOptions(['CF DE RECEBÍVEIS', 'CF DA CONTA ESCROW', '1ºADIT', '4ADIT-CPRF_2ADIT-CF', 'ADITAMENTO', 'n/a']);
const COURT_OPTIONS = toOptions(['SIM', 'NÃO']);

const SectionTitle = ({ children, color }: { children: string; color?: string }) => (
    <h3 className={`text-[10px] font-semibold uppercase mb-3 tracking-wide ${color || 'text-gray-500 dark:text-gray-400'}`}>{children}</h3>
);

const GuaranteePageForm = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [form, setForm] = useState<any>({});

    useEffect(() => {
        if (!id) return;
        setIsLoading(true);
        collateralApi.getGuaranteeView(id)
            .then(setForm)
            .catch((e: any) => {
                if (e instanceof ApiError && e.status === 404) return navigate('/not-found');
                navigate('/error');
            })
            .finally(() => setIsLoading(false));
    }, [id]);

    const set = (field: string, value: any) => setForm((prev: any) => ({ ...prev, [field]: value }));
    const ev = (field: string) => (e: any) => set(field, e.target.value);

    const handleSave = async () => {
        try {
            await collateralApi.updateGuarantee(id!, form);
            toast.success('Garantia atualizada!');
            navigate(`/collateral/guarantees/${id}/view`);
        } catch {
            toast.error('Erro ao salvar');
        }
    };

    if (isLoading) return <Loading />;

    return (
        <PageForm
            pageTitle={`Editar — ${form.operationCode || ''}`}
            apiVersion={form.apiVersion ? `${form.apiName} - ${form.apiVersion} (${(form.environment || '').toUpperCase()})` : ''}
            isLoading={false}
            breadcrumb={[
                COLLATERAL_BREADCRUMBS.guarantees,
                { label: form.operationCode, url: `/collateral/guarantees/${id}/view` },
                { label: 'Editar', url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end space-x-2">
                    <button onClick={() => navigate(`/collateral/guarantees/${id}/view`)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600 text-sm">
                        Cancelar
                    </button>
                    <button onClick={handleSave}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm flex items-center space-x-2">
                        <FiSave size={14} /> <span>Salvar</span>
                    </button>
                </div>
            }
        >
            <div className="grid grid-cols-[2fr_1fr] gap-4 my-4">
                {/* Configuração */}
                <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-4 border border-green-200 dark:border-green-800">
                    <SectionTitle color="text-green-700 dark:text-green-400">Configuração</SectionTitle>
                    <div className="grid grid-cols-2 gap-x-6 divide-x divide-green-200 dark:divide-green-800">
                        <div className="space-y-3">
                            <SelectTextField label="Tipo operação" value={form.operationType} items={OPERATION_TYPES} onChange={ev('operationType')} disabled />
                            <SelectTextField label="Obrigação" value={form.obligation} items={OBLIGATION_TYPES} onChange={ev('obligation')} disabled />
                            <TextEdit label="Cedente" value={form.assignor || ''} onChange={ev('assignor')} disabled />
                            <TextEdit label="CNPJ Cedente" value={form.assignorTaxId || ''} onChange={ev('assignorTaxId')} disabled />
                            <TextEdit label="Risco (R$)" value={form.assignorRisk || ''} onChange={ev('assignorRisk')} type="number" disabled />
                            <TextEdit label="Risco (%)" value={form.assignorRiskPercentage || ''} onChange={ev('assignorRiskPercentage')} type="number" disabled />
                            <TextEdit label="Principais sacados" value={form.mainDebtors || ''} onChange={ev('mainDebtors')} />
                            <TextEdit label="Data emissão" value={form.issueDate?.split('T')[0] || ''} onChange={ev('issueDate')} type="date" disabled />
                            <SelectTextField label="Critério análise" value={form.analysisCriteria} items={CRITERIA_OPTIONS} onChange={ev('analysisCriteria')} />
                        </div>
                        <div className="space-y-3 pl-6">
                            <TextEdit label="Credora" value={form.creditor || ''} onChange={ev('creditor')} />
                            <TextEdit label="Resp. análise" value={form.analystName || ''} onChange={ev('analystName')} />
                            <TextEdit label="Trader" value={form.trader || ''} onChange={ev('trader')} disabled />
                            <TextEdit label="Resp. validação" value={form.validationAnalystName || ''} onChange={ev('validationAnalystName')} disabled />
                            <TextEdit label="Pendência" value={form.pendency || ''} onChange={ev('pendency')} />
                            <TextEdit label="Observação" value={form.observation || ''} onChange={ev('observation')} />
                            <TextEdit label="Data com trader" value={form.traderHandoverDate?.split('T')[0] || ''} onChange={ev('traderHandoverDate')} type="date" />
                            <MultiEmailInput label="Follow up áreas" value={form.followUpAreas ? form.followUpAreas.split(';').map((s: string) => s.trim()).filter(Boolean) : []} onChange={(vals: string[]) => set('followUpAreas', vals.join('; '))} placeholder="Digite a data e pressione Enter" />
                        </div>
                    </div>
                </div>

                {/* Right: Resumo (read-only) + Status */}
                <div className="flex flex-col gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                        <SectionTitle color="text-blue-700 dark:text-blue-400">Resumo (somente leitura)</SectionTitle>
                        <div className="space-y-1 text-xs">
                            <div className="flex justify-between"><span className="text-gray-500">Nº títulos</span><span>{form.totalTitles}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Moeda</span><span>{form.currency || 'BRL'}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Total</span><span>{formatCurrency(form.totalAmount || 0)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">% validado</span><span>{form.validatedPercentage || 0}%</span></div>
                        </div>
                    </div>

                    <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                        <SectionTitle color="text-amber-700 dark:text-amber-400">Status</SectionTitle>
                        <div className="space-y-3">
                            <SelectTextField label="Status garantia" value={form.guaranteeStatus} items={GUARANTEE_STATUSES} onChange={ev('guaranteeStatus')} />
                            <SelectTextField label="Status trader" value={form.statusTrader} items={TRADER_STATUSES} onChange={ev('statusTrader')} />
                            <div className="text-xs text-gray-500 pt-2">Status validação: <span className="dark:text-white font-medium">{form.validationStatus || '—'}</span> (somente leitura)</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Rosa + Roxo */}
            <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded">
                    <SelectTextField label="Lastro salvo em garantias?" value={form.collateralSaved} items={COLLATERAL_SAVED_OPTIONS} onChange={ev('collateralSaved')} />
                </div>
                <div className="p-3 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded">
                    <SelectTextField label="Notificação sacados" value={form.debtorNotification} items={DEBTOR_NOTIFICATION_OPTIONS} onChange={ev('debtorNotification')} />
                </div>
                <div className="p-3 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded">
                    <div className="grid grid-cols-2 gap-4">
                        <SelectTextField label="Registro em cartório" value={form.courtRegistration ? 'SIM' : 'NÃO'} items={COURT_OPTIONS} onChange={(e: any) => set('courtRegistration', e.target.value === 'SIM')} />
                        <SelectTextField label="Qual contrato registrar?" value={form.contractToRegister} items={CONTRACT_REGISTER_OPTIONS} onChange={ev('contractToRegister')} />
                    </div>
                </div>
            </div>

            {/* Documentos */}
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <SectionTitle>Checklist Documental</SectionTitle>
                <div className="grid grid-cols-4 gap-4">
                    {[
                        { key: 'xml', label: 'XML' },
                        { key: 'nfPdf', label: 'NF PDF' },
                        { key: 'canhoto', label: 'Canhoto' },
                        { key: 'duplicata', label: 'Duplicata com aceite' },
                    ].map(doc => {
                        const d = form[doc.key] || {};
                        return (
                            <div key={doc.key} className="border border-gray-100 dark:border-slate-700 rounded p-3 space-y-3">
                                <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase">{doc.label}</span>
                                <SelectTextField label="Status" value={d.status} items={DOC_STATUSES} onChange={(e: any) => set(doc.key, { ...d, status: e.target.value })} />
                                <TextEdit label="Arquivados" value={d.archived ?? 0} onChange={(e: any) => set(doc.key, { ...d, archived: +e.target.value })} type="number" />
                                <TextEdit label="Pendentes" value={d.pending ?? 0} onChange={(e: any) => set(doc.key, { ...d, pending: +e.target.value })} type="number" />
                            </div>
                        );
                    })}
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                    <SelectTextField label="SEFAZ" value={form.sefaz} items={SEFAZ_STATUSES} onChange={ev('sefaz')} />
                    <TextEdit label="Títulos amostragem" value={form.sampledTitles ?? 0} onChange={ev('sampledTitles')} type="number" />
                    <MultiEmailInput label="Links" value={form.links ? form.links.split(';').map((s: string) => s.trim()).filter(Boolean) : []} onChange={(vals: string[]) => set('links', vals.join('; '))} placeholder="Digite a URL e pressione Enter" />
                </div>
            </div>
        </PageForm>
    );
};

export default GuaranteePageForm;
